import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import HeroSlider from '../components/HeroSlider';
import CaseCard from '../components/CaseCard';
import GlobalReach from '../components/GlobalReach';
import { cases, services } from '../data/mockData';
import { ChevronRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../translations/translations';

const Home = () => {
  const { language, paths, buildPath } = useLanguage();
  const t = translations[language];
  
  return (
    <div className="bg-white">
      {/* 1. Hero Slider */}
      <HeroSlider />

      {/* 2. What We Believe Section - Large Prominent Text */}
      <section className="py-20 px-6 lg:px-16 bg-[#ff5200]">
        <div className="max-w-[1600px] mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-white"
          >
            <h2 className="text-[clamp(1.5rem,3vw,3rem)] font-bold leading-[1.15] uppercase mb-4">
              {t.home.whatWeBelieve}
            </h2>
            {t.home.whatWeBelievelStatements.map((statement, index) => (
              <p key={index} className="text-[clamp(1.5rem,3vw,3rem)] font-bold leading-[1.15] uppercase mb-3">
                {statement}
              </p>
            ))}
          </motion.div>
        </div>
      </section>

      {/* 3. Our Work Section - Full-Width Visual Showcase (No Text Overlay) */}
      <section className="bg-white">
        <div className="max-w-full">
          {cases.map((caseItem, index) => (
            <Link
              key={caseItem.id}
              to={buildPath('caseDetail', { slug: caseItem.slug })}
              className="block relative overflow-hidden group"
            >
              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="relative w-full"
              >
                {/* Full Image - No Text Overlay */}
                <div className="relative overflow-hidden bg-gray-100">
                  <img
                    src={caseItem.image}
                    alt={caseItem.title}
                    className="w-full h-auto object-contain transition-transform duration-300 ease-out group-hover:scale-105"
                  />
                </div>
              </motion.div>
            </Link>
          ))}
        </div>
      </section>

      {/* 4. Who is Pik Section */}
      <section className="py-20 px-6 lg:px-12 bg-white">
        <div className="max-w-5xl mx-auto text-center">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl md:text-5xl font-bold text-black mb-8"
          >
            {t.home.whoIsPik}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-lg md:text-xl text-gray-700 leading-relaxed"
          >
            {t.home.whoIsPikText}
          </motion.p>
        </div>
      </section>

      {/* 5. Global Reach Section - We pik up across borders */}
      <GlobalReach />

      {/* 6. What We Do Section */}
      <section className="py-20 px-6 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-black mb-4">
              {t.home.whatWeDo}
            </h2>
            <p className="text-xl text-gray-600">
              {t.home.whatWeDoSubtitle}
            </p>
          </motion.div>

          {/* Service List - Flat Minimal Design with Text Color Hover */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {services.slice(0, 6).map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="bg-white p-8 group cursor-pointer"
              >
                <h3 className="text-xl font-bold mb-4 text-black group-hover:text-[#ff5200] transition-colors duration-300 ease-in-out">
                  {language === 'de' ? service.titleDe : service.title}
                </h3>
                <ul className="space-y-2.5">
                  {(language === 'de' ? service.itemsDe : service.items).map((item, idx) => (
                    <li key={idx} className="text-sm text-gray-700 flex items-start gap-2">
                      <span className="text-[#ff5200] mt-1 font-bold group-hover:text-[#ff5200] transition-colors duration-300 ease-in-out">•</span>
                      <span className="group-hover:text-[#ff5200] transition-colors duration-300 ease-in-out">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>

          {/* View All Services Button */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <Link
              to={paths.services}
              className="inline-flex items-center gap-2 bg-[#ff5200] text-white px-8 py-4 rounded-full font-semibold hover:bg-[#e64a00] transition-colors duration-300"
            >
              {t.home.viewAllServices}
              <ChevronRight size={20} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* 7. CTA Section - Let's Pik up your brands */}
      <section className="py-20 px-6 lg:px-12 bg-black text-white">
        <div className="max-w-4xl mx-auto text-center">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl md:text-6xl font-bold mb-8"
          >
            {t.home.letsPickUp}
          </motion.h2>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <Link
              to={paths.contact}
              className="inline-flex items-center gap-2 bg-[#ff5200] text-white px-10 py-5 rounded-full font-semibold text-lg hover:bg-[#e64a00] transition-colors duration-300"
            >
              {t.home.getInTouch}
              <ChevronRight size={24} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* 8. Footer automatically follows */}
    </div>
  );
};

export default Home;